<template>
  <div class="color-picker-wrapper">
    <div class="flex items-center gap-3">
      <div ref="colorPickerContainer" class="color-picker-trigger" :style="triggerStyle"></div>
      <div class="flex-1">
        <input
          v-model="hexValue"
          type="text"
          class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm"
          placeholder="#FFFFFF"
          @input="handleHexInput"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import Pickr from '@simonwep/pickr'
import '@simonwep/pickr/dist/themes/classic.min.css'

const props = defineProps({
  modelValue: {
    type: String,
    default: '#FEF3C7'
  }
})

const emit = defineEmits(['update:modelValue'])

const colorPickerContainer = ref(null)
const hexValue = ref(props.modelValue || '#FFFFFF')
let pickr = null

const triggerStyle = computed(() => {
  const color = hexValue.value || props.modelValue || '#FFFFFF'
  return {
    backgroundColor: color
  }
})

const initPicker = () => {
  if (!colorPickerContainer.value || pickr) return

  pickr = Pickr.create({
    el: colorPickerContainer.value,
    theme: 'classic',
    default: props.modelValue,
    useAsButton: true,
    inline: false,
    container: document.body,
    swatches: [
      '#2D4D31',
      '#414B3B',
      '#7D9B76',
      '#8CA87C',
      '#CBDDD1',
      '#EFE7DD',
      '#FFF5EA',
      '#FFF9F3',
      '#E6D6CD',
      '#F6F4F1',
      '#795833',
      '#96695E',
      '#B29E84',
      '#DDC1A7',
      '#CEDEEB',
      '#CAE8FF',
      '#FFD3DB',
      '#FFEBEB',
      '#E8E8E8',
      '#EDE7E3',
      '#FFFFFF',
      '#000000'
    ],
    components: {
      preview: true,
      opacity: false,
      hue: true,
      interaction: {
        hex: true,
        rgba: true,
        hsla: false,
        hsva: false,
        cmyk: false,
        input: true,
        clear: false,
        save: true
      }
    }
  })

  pickr.on('save', (color) => {
    if (color) {
      const hex = color.toHEXA().toString()
      hexValue.value = hex
      emit('update:modelValue', hex)
      pickr.hide()
    }
  })

  pickr.on('change', (color) => {
    if (color) {
      const hex = color.toHEXA().toString()
      hexValue.value = hex
      emit('update:modelValue', hex)
    }
  })
}

const handleHexInput = (event) => {
  const value = event.target.value
  if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
    hexValue.value = value
    if (value.length === 7 && pickr) {
      pickr.setColor(value)
      emit('update:modelValue', value)
    }
  }
}

watch(() => props.modelValue, (newValue) => {
  hexValue.value = newValue
  if (pickr && newValue) {
    pickr.setColor(newValue)
  }
})

onMounted(() => {
  initPicker()
})

onBeforeUnmount(() => {
  if (pickr) {
    pickr.destroy()
    pickr = null
  }
})
</script>

<style scoped>
.color-picker-wrapper {
  position: relative;
}

.color-picker-trigger {
  width: 4rem;
  height: 2.5rem;
  border-radius: 0.375rem;
  border: 2px solid #d1d5db;
  cursor: pointer;
  transition: all 0.2s;
}

.color-picker-trigger:hover {
  border-color: #9ca3af;
}

:deep(.pcr-app) {
  background: white;
  border-radius: 8px;
  z-index: 1000;
}

:deep(.pcr-button) {
  width: 4rem;
  height: 2.5rem;
  border-radius: 0.375rem;
  border: 2px solid #d1d5db;
  cursor: pointer;
}
</style>

